import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { BookMarked, AlertTriangle } from "lucide-react";

// Live guideline dose chips + contraindication alerts under the Rx table.
// Data comes from doctor-pasted guideline drug tables (Guidelines page).
export default function GuidelineDoseStrip({ medicines, known = [], complications = [], onApplyDose }) {
  const [tables, setTables] = useState([]);
  useEffect(() => {
    api.get("/guideline-tables").then((r) => setTables(r.data.tables || [])).catch(() => {});
  }, []);

  const matches = useMemo(() => {
    const out = [];
    const ctxText = `${known.join(" ")} ${complications.join(" ")}`.toLowerCase();
    const rxNames = (medicines || []).map((m) => `${m.brand_name || ""} ${m.generic_name || ""} ${m.composition || ""}`.toLowerCase());
    (medicines || []).forEach((m, i) => {
      const t = rxNames[i];
      if (!t.trim()) return;
      for (const tab of tables) {
        for (const row of tab.rows || []) {
          const dn = (row.drug || "").toLowerCase().trim();
          if (dn.length < 4 || !t.includes(dn)) continue;
          const contra = (row.contra || "").toLowerCase();
          const alerts = [];
          // other prescribed drugs mentioned in the contraindication text
          rxNames.forEach((other, j) => {
            if (j === i || !other.trim()) return;
            for (const w of other.split(/[\s+(),/]+/)) {
              if (w.length >= 5 && contra.includes(w)) {
                alerts.push(`co-prescribed ${(medicines[j].brand_name || medicines[j].generic_name || "").trim()} appears in its contraindications`);
                break;
              }
            }
          });
          // patient conditions mentioned in the contraindication text
          for (const kw of ["seizure", "hepatic", "liver", "renal", "glaucoma", "anorexia", "bulimia", "mania", "qt", "hypertension", "pregnan"]) {
            if (contra.includes(kw) && ctxText.includes(kw)) alerts.push(`patient context matches "${kw}" contraindication`);
          }
          out.push({ i, drug: m.brand_name || m.generic_name, guideline: tab.guideline, dose: row.dose, contra: row.contra, alerts });
        }
      }
    });
    return out;
  }, [medicines, tables, known, complications]);

  if (!matches.length) return null;
  return (
    <div className="px-3 py-2 border-t border-slate-200 space-y-1.5" data-testid="guideline-dose-strip">
      {matches.map((x, idx) => (
        <div key={idx} className="text-xs">
          <div className="flex items-center gap-2 flex-wrap">
            <BookMarked size={12} className="text-indigo-600 shrink-0" />
            <span className="font-medium">{x.drug}</span>
            <span className="text-slate-500">{x.guideline}:</span>
            <span className="tabular text-indigo-800">{x.dose || "—"}</span>
            {x.dose && (
              <button
                onClick={() => onApplyDose(x.i, x.dose)}
                className="px-1.5 py-0.5 rounded border border-indigo-300 text-indigo-700 hover:bg-indigo-50 text-[10px]"
                data-testid={`apply-guideline-dose-${x.i}`}
              >
                Apply dose
              </button>
            )}
            {x.contra && <span className="text-[10px] text-slate-400 truncate max-w-[380px]" title={x.contra}>CI: {x.contra}</span>}
          </div>
          {x.alerts.map((a, k) => (
            <div key={k} className="mt-1 ml-5 px-2 py-1 rounded alert-critical flex items-center gap-1.5 text-[11px]" data-testid="guideline-contra-alert">
              <AlertTriangle size={11} /> {x.guideline}: {x.drug} — {a}
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
